import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { Link, List, StickyNote, X } from "lucide-react";
import { resolveSuggestion } from "../document/blockUtils";
import { BlockReferences } from "./BlockReferences";
import { SuggestionReferences } from "./SuggestionReferences";
import { TableOfContent } from "./toc/TableOfContent";

export const ReaderPanel = () => {
  const document = useAppState((state) => state.currentDocument);
  const selectedBlockId = useAppState((state) => state.selectedBlockId);
  const referencePanel = useAppState((state) => state.referencePanel);
  const dispatch = useAppDispatch();
  const suggestion =
    document && selectedBlockId
      ? resolveSuggestion(document, selectedBlockId)
      : null;

  if (!document || !referencePanel.visible) return null;

  const handleTabChange = (view: string) => {
    if (view === 'toc' || view === 'references' || view === 'notes') {
      dispatch({
        type: "SHOW_REFERENCE_PANEL",
        payload: { visible: true, view }
      });
    }
  };

  const handleClose = () => {
    dispatch({
      type: "SHOW_REFERENCE_PANEL",
      payload: { visible: false }
    });
  };

  return (
    <aside className="fixed top-0 right-0 w-[400px] h-screen bg-white border-l border-gray-200 shadow-lg z-50 flex flex-col">
      {/* Fixed Header */}
      <Tabs
        value={referencePanel.view}
        onValueChange={handleTabChange}
        className="flex flex-col h-full"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg">
              {referencePanel.view === "toc" && "Table of contents"}
              {referencePanel.view === "references" && "References"}
              {referencePanel.view === "notes" && "My notes"}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <TabsList className="bg-transparent gap-1">
              <TabsTrigger value="toc" className="px-2 data-[state=active]:bg-accent data-[state=active]:shadow-none cursor-pointer">
                <List className="h-4 w-4" />
              </TabsTrigger>
              <TabsTrigger value="references" className="px-2 data-[state=active]:bg-accent data-[state=active]:shadow-none cursor-pointer">
                <Link className="h-4 w-4" />
              </TabsTrigger>
              <TabsTrigger value="notes" className="px-2 data-[state=active]:bg-accent data-[state=active]:shadow-none cursor-pointer">
                <StickyNote className="h-4 w-4 " />
              </TabsTrigger>
            </TabsList>
            <X 
              className="h-4 w-4 ml-2 text-gray-500 hover:text-gray-700 cursor-pointer" 
              onClick={handleClose}
            />
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="h-full overflow-hidden pt-4">
            {/* Table of Contents Tab */}
            <TabsContent value="toc" className="h-full mt-0">
              <div className="h-full overflow-y-auto px-4 pb-4">
                <TableOfContent />
              </div>
            </TabsContent>

            {/* Block References Tab */}
            <TabsContent value="references" className="h-full mt-0">
              <div className="h-full overflow-y-auto px-4 pb-4">
                <BlockReferences
                  document={document}
                  suggestion={suggestion}
                  placeholder="Block references appear here when you view an AI suggestion that quotes more than one Block."
                />
              </div>
            </TabsContent>

            {/* Suggestion References / Notes Tab */}
            <TabsContent value="notes" className="h-full mt-0">
              <div className="h-full overflow-y-auto px-4 pb-4">
                <SuggestionReferences />
              </div>
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </aside>
  );
};
